// SEQ-232: the one read-only query behind "No costs entered". See
// projectMonthCosts.ts for the rule and for why a short answer is never trusted.
//
// It selects project_id and month_date, nothing else, with source = 'manual'
// stated explicitly: Xero rows are never read or counted. No RPC, no write, no
// migration. Row level security on claims_pnl_monthly_values already limits
// what the signed in user can see to the projects they can open.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { loadManualCostKeys, monthAfter, type ManualCostCheck, type ManualRowPage } from "./projectMonthCosts";

export interface ManualCostMonthsResult {
  /** True once the read for the CURRENT inputs has finished, either way. */
  settled: boolean;
  check: ManualCostCheck;
}

const NOT_YET: ManualCostCheck = { status: "unavailable" };

/**
 * `projectIds` are the projects the month rows came back for, `months` the
 * ascending 'YYYY-MM-01' list they cover. Disabled (or nothing to ask about)
 * reads as settled and unavailable, so nothing is marked.
 */
export function useManualCostMonths(projectIds: string[], months: string[] | null, enabled: boolean): ManualCostMonthsResult {
  const requestKey = useMemo(
    () => (enabled && months && months.length > 0 && projectIds.length > 0
      ? JSON.stringify({ p: [...projectIds].sort(), from: months[0], to: months[months.length - 1] })
      : null),
    [enabled, months, projectIds],
  );
  const [state, setState] = useState<{ key: string | null; check: ManualCostCheck }>({ key: null, check: NOT_YET });

  useEffect(() => {
    if (requestKey === null) return;
    const { p, from, to } = JSON.parse(requestKey) as { p: string[]; from: string; to: string };
    let cancelled = false;
    const upper = monthAfter(to);
    const fetchPage = async (start: number, end: number): Promise<ManualRowPage> => {
      // `source` is not in the generated database types, so the builder is untyped here on purpose.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, count, error } = await (supabase as any)
        .from("claims_pnl_monthly_values")
        .select("project_id, month_date", { count: "exact" })
        .eq("source", "manual")
        .in("project_id", p)
        .gte("month_date", from)
        .lt("month_date", upper)
        .order("month_date", { ascending: true })
        .order("id", { ascending: true })
        .range(start, end);
      return { rows: error ? null : (data as ManualRowPage["rows"]), count: count ?? null, error: error ? String(error.message ?? error) : null };
    };
    loadManualCostKeys(fetchPage).then((check) => {
      if (!cancelled) setState({ key: requestKey, check });
    });
    return () => {
      cancelled = true;
      // Forget the answer when the inputs change or By month is left, so coming back never reads an old one as settled.
      setState({ key: null, check: NOT_YET });
    };
  }, [requestKey]);

  if (requestKey === null) return { settled: true, check: NOT_YET };
  return { settled: state.key === requestKey, check: state.key === requestKey ? state.check : NOT_YET };
}
